import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, ShieldCheck, History, PlugZap } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useCompany, fmtNipc } from "../../context/CompanyContext";
import { CompanyField, CompanySection, LogoUploader } from "../../components/company/CompanyFormParts";

const FIELDS = [
  "legal_name", "trade_name", "short_name", "slogan", "nipc", "address", "postal_code",
  "city", "country", "iban", "bank", "bic", "mbway", "phone", "whatsapp", "email",
  "website", "share_capital", "registry", "invoicing_start_date", "facturalusa_serie",
];

function LegacyCard({ legacy }) {
  if (!legacy) return null;
  return (
    <section className="lux-card p-6 md:p-8 border-l-2 border-l-[#d4af37]/60" data-testid="company-legacy-card">
      <div className="eyebrow mb-2 flex items-center gap-2"><History size={12} /> Entidade anterior</div>
      <div className="font-display text-xl">{legacy.legal_name} <span className="text-sm text-[#a0b2af]">(atividade individual)</span></div>
      <div className="text-sm text-[#a0b2af] mt-2">NIF {fmtNipc(legacy.nipc)} · IBAN <span className="font-mono">{legacy.iban}</span></div>
      <p className="text-xs text-[#7d918d] mt-4 max-w-2xl leading-relaxed">
        Faturas, notas de crédito e contratos já emitidos continuam associados a esta entidade e
        nunca são alterados. Os novos documentos usam os dados abaixo e guardam uma cópia
        («snapshot») da entidade emissora no momento da emissão.
      </p>
    </section>
  );
}

function FacturalusaCheck() {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  async function run() {
    setBusy(true);
    try { setRes((await api.get("/admin/company/facturalusa-check")).data); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }
  return (
    <div className="md:col-span-2 flex items-center gap-4 flex-wrap">
      <button type="button" onClick={run} disabled={busy}
              className="px-4 py-2 text-xs inline-flex items-center gap-2 border border-[#d4af37]/60 text-[#d4af37] rounded-sm hover:bg-[#d4af37]/10 transition-colors duration-200 disabled:opacity-60"
              data-testid="company-facturalusa-check-btn">
        <PlugZap size={14} /> {busy ? "A testar…" : "Testar ligação Facturalusa"}
      </button>
      {res && (
        <span className={`text-xs ${res.ok ? "text-emerald-300" : "text-red-300"}`} data-testid="company-facturalusa-check-result">
          {res.ok ? "Ligação OK" : `Erro: ${res.error}`} · conta {res.account === "company" ? "nova empresa" : "anterior (.env)"} · série {res.serie}
          {res.ok && !res.serie_found ? " (série ainda não criada)" : ""}
        </span>
      )}
    </div>
  );
}

export default function AdminCompany() {
  const { reload } = useCompany();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(false);

  const apply = (d) => {
    setData(d);
    setForm(Object.fromEntries(FIELDS.map((k) => [k, d[k] ?? ""])));
    reload();
  };

  useEffect(() => {
    api.get("/admin/company").then(({ data: d }) => apply(d)).catch((e) => toast.error(formatApiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const f = (k, label, extra = {}) => (
    <CompanyField label={label} value={form[k]} onChange={set(k)} testId={`company-${k.replace(/_/g, "-")}-input`} {...extra} />
  );

  async function save(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, ...(apiKey ? { facturalusa_api_key: apiKey } : {}) };
      const { data: d } = await api.put("/admin/company", payload);
      apply(d);
      setApiKey("");
      toast.success("Dados da empresa guardados. Aplicados a e-mails, PDFs e novas faturas.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  }

  async function removeKey() {
    setBusy(true);
    try {
      const { data: d } = await api.put("/admin/company", { ...form, facturalusa_api_key: "" });
      apply(d);
      toast.success("Chave removida — volta a usar a conta Facturalusa anterior.");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  }

  if (!data) return <div className="text-sm text-[#a0b2af]" data-testid="company-loading">A carregar…</div>;

  return (
    <form onSubmit={save} className="space-y-8 max-w-5xl pb-28" data-testid="admin-company-page">
      <div>
        <div className="eyebrow mb-3 flex items-center gap-2"><Building2 size={12} /> Configuração central</div>
        <h1 className="font-display text-4xl sm:text-5xl">Dados da Empresa.</h1>
        <p className="text-sm text-[#a0b2af] mt-3 max-w-2xl">
          Fonte única para nome, NIPC, IBAN, contactos e logótipo. Tudo o que for alterado aqui
          aplica-se automaticamente a e-mails, PDFs, instruções de pagamento e novas faturas.
        </p>
      </div>

      <LegacyCard legacy={data.legacy_issuer} />

      <CompanySection eyebrow="Identidade" title="Nome e marca" testId="company-section-identity">
        {f("legal_name", "Denominação legal")}
        {f("trade_name", "Nome comercial (marca)")}
        {f("short_name", "Nome curto", { hint: "Usado em textos curtos (ex.: «equipa Lux Madeira»)." })}
        {f("slogan", "Slogan")}
      </CompanySection>

      <CompanySection eyebrow="Fiscal" title="Dados fiscais e sede" testId="company-section-fiscal">
        {f("nipc", "NIPC", { placeholder: "9 dígitos", mono: true })}
        {f("address", "Morada fiscal")}
        {f("postal_code", "Código postal", { placeholder: "9000-000" })}
        {f("city", "Localidade")}
        {f("country", "País")}
        {f("share_capital", "Capital social (opcional)", { placeholder: "ex.: 5.000,00 €" })}
        <div className="md:col-span-2">{f("registry", "Conservatória / Matrícula (opcional)")}</div>
      </CompanySection>

      <CompanySection eyebrow="Pagamentos" title="Dados bancários" testId="company-section-bank">
        <div className="md:col-span-2">{f("iban", "IBAN", { mono: true, placeholder: "PT50 0000 0000 0000 0000 0000 0" })}</div>
        {f("bank", "Banco")}
        {f("bic", "BIC / SWIFT", { mono: true })}
        {f("mbway", "MB WAY", { mono: true })}
      </CompanySection>

      <CompanySection eyebrow="Contactos" title="Contactos públicos" testId="company-section-contacts">
        {f("phone", "Telefone")}
        {f("whatsapp", "WhatsApp")}
        {f("email", "E-mail de contacto", { type: "email" })}
        {f("website", "Website")}
      </CompanySection>

      <section className="lux-card p-6 md:p-8 space-y-6" data-testid="company-section-logos">
        <div>
          <div className="eyebrow mb-2">Marca</div>
          <h2 className="font-display text-2xl">Logótipos</h2>
          <p className="text-xs text-[#7d918d] mt-2">PNG com fundo transparente recomendado · máx. 5 MB.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <LogoUploader variant="light" url={data.logo_url} custom={!!data.logo_path} onChange={apply} />
          <LogoUploader variant="dark" url={data.logo_dark_url} custom={!!data.logo_dark_path} onChange={apply} />
        </div>
      </section>

      <CompanySection eyebrow="Faturação" title="Facturalusa — nova conta" testId="company-section-facturalusa">
        <div className="md:col-span-2 text-xs text-[#a0b2af] flex items-start gap-2">
          <ShieldCheck size={14} className="text-[#d4af37] shrink-0 mt-0.5" />
          <span>
            O NIPC emissor das faturas é o da conta Facturalusa. Enquanto não indicar a chave da nova
            conta, as faturas continuam a ser emitidas pela conta anterior. Faturas antigas (PDFs e notas
            de crédito) usam sempre a conta onde foram emitidas.
          </span>
        </div>
        <CompanyField
          label={`Chave API Facturalusa ${data.facturalusa_api_key_set ? `(definida ${data.facturalusa_api_key_hint})` : "(não definida)"}`}
          value={apiKey} onChange={setApiKey} type="password" mono
          placeholder={data.facturalusa_api_key_set ? "Deixe vazio para manter" : "Cole a chave da nova conta"}
          testId="company-facturalusa-api-key-input"
        />
        {f("facturalusa_serie", "Série (opcional)", { placeholder: "A", hint: "Vazio = série padrão." })}
        {f("invoicing_start_date", "Início de faturação da nova empresa", { type: "date", hint: "A nova conta só é usada a partir desta data." })}
        {data.facturalusa_api_key_set && (
          <div className="flex items-end">
            <button type="button" onClick={removeKey} disabled={busy}
                    className="px-4 py-3 text-xs border border-[#1a4640] rounded-sm hover:border-red-400 hover:text-red-300 transition-colors duration-200"
                    data-testid="company-facturalusa-remove-key-btn">
              Remover chave
            </button>
          </div>
        )}
        <FacturalusaCheck />
      </CompanySection>

      <div className="fixed bottom-0 left-0 right-0 md:left-auto md:right-8 md:bottom-6 z-30 p-4 md:p-0 bg-[#041f1b]/90 md:bg-transparent backdrop-blur-md md:backdrop-blur-0 border-t border-[#1a4640] md:border-0">
        <button type="submit" disabled={busy}
                className="btn-gold w-full md:w-auto px-8 py-3 text-sm shadow-lg disabled:opacity-60"
                data-testid="company-save-btn">
          {busy ? "A guardar…" : "Guardar dados da empresa"}
        </button>
      </div>
    </form>
  );
}
